import { useCallback, useLayoutEffect, useRef } from "react";

type Cache = { delete: (key: string) => void };
type RecordInfo = { user: string | number; recency: number };
type Owner = { key: string; user: string | number };
type State = { records: Map<string, RecordInfo>; owners: Map<object, Owner>; sequence: number };
const states = new WeakMap<Cache, State>();
const MAX_RECORDS = 2;

function stateFor(cache: Cache): State {
  let state = states.get(cache);
  if (!state) {
    state = { records: new Map(), owners: new Map(), sequence: 0 };
    states.set(cache, state);
  }
  return state;
}

function prune(cache: Cache, state: State) {
  const activeKeys = new Set(Array.from(state.owners.values(), owner => owner.key));
  const activeUsers = new Set(Array.from(state.owners.values(), owner => owner.user));
  const inactive = Array.from(state.records.entries()).filter(([key]) => !activeKeys.has(key))
    .sort((a, b) => b[1].recency - a[1].recency);
  let room = Math.max(0, MAX_RECORDS - activeKeys.size);
  for (const [key, info] of inactive) {
    if (room > 0 && (!activeUsers.size || activeUsers.has(info.user))) {
      room--;
      continue;
    }
    // Only keys registered by this hook are touched; unrelated SWR data stays.
    cache.delete(key);
    state.records.delete(key);
  }
}

/** Keep at most two owned historical results, plus any concurrently active owners. */
export default function useHomeHistoryCache(cache: Cache, key: string, user: number | string | undefined) {
  const owner = useRef({});
  // Register before SWR's layout effects can start an immediately resolved fetch.
  useLayoutEffect(() => {
    const state = stateFor(cache);
    if (key && user) {
      state.owners.set(owner.current, { key, user });
      state.records.set(key, { user, recency: ++state.sequence });
    }
    prune(cache, state);
    return () => {
      state.owners.delete(owner.current);
      prune(cache, state);
    };
  }, [cache, key, user]);

  return useCallback(() => {
    // SWR publishes the resolved promise in its following microtask. Prune after
    // that publication so a late obsolete request cannot recreate an evicted key.
    setTimeout(() => {
      const state = stateFor(cache);
      if (key && user && !state.records.has(key)) state.records.set(key, { user, recency: 0 });
      prune(cache, state);
    }, 0);
  }, [cache, key, user]);
}
